import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, AlertTriangle, CheckCircle, Clock, FileText } from 'lucide-react';
import { SEOLayout, AlertBox } from '../../components/SEOLayout';
import { allCountriesData as countriesData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';

const DestinationsHub = () => {
  const countries = Object.values(countriesData);

  const CountryCard = ({ country }) => (
    <Link 
      to={`/destinations/${country.slug}`}
      className="block bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{country.flag}</span>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{country.name}</h3>
            <p className="text-slate-500 text-sm">{country.code}</p>
          </div>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          {country.importRules.quarantine ? (
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="w-4 h-4" />
              Quarantine: {typeof country.importRules.quarantineDays === 'number' 
                ? `${country.importRules.quarantineDays} days` 
                : country.importRules.quarantineDays}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-4 h-4" />
              No quarantine
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {country.importRules.importPermit ? (
            <span className="flex items-center gap-1 text-amber-600">
              <FileText className="w-4 h-4" />
              Import permit required
            </span>
          ) : (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-4 h-4" />
              No permit needed
            </span>
          )}
        </div>
      </div>
      
      <div className="mt-4 text-blue-600 text-sm font-medium">
        View full requirements →
      </div>
    </Link>
  );

  // Group countries by difficulty
  const easyCountries = countries.filter(c => !c.importRules.quarantine && !c.importRules.importPermit);
  const moderateCountries = countries.filter(c => !c.importRules.quarantine && c.importRules.importPermit);
  const strictCountries = countries.filter(c => c.importRules.quarantine);

  return (
    <SEOLayout
      title="Pet Import Requirements by Country 2026 | Complete Destination Guide"
      description="Complete guide to pet import rules for every country. Quarantine requirements, rabies rules, import permits, banned breeds. Know before you travel."
      keywords={['pet import requirements', 'bring pet to country', 'pet quarantine', 'international pet travel', 'pet import permit']}
      breadcrumbs={[{ label: 'Destinations' }]}
    >
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Globe className="w-4 h-4" />
          Updated for 2026
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Pet Import Requirements by Country
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Every country has different rules for importing pets. Some require months of preparation, 
          quarantine, and blood tests. Others are straightforward. 
          <span className="text-red-600 font-semibold"> Get it wrong, and your pet could be quarantined, 
          returned, or worse.</span>
        </p>
      </div>

      {/* Warning Box */}
      <div className="mb-12">
        <AlertBox type="warning" title="Start Early - Some Countries Require 6+ Months Preparation">
          <p>
            Countries like <strong>Australia, Japan, Singapore, and New Zealand</strong> require blood tests, 
            multiple vaccinations, and waiting periods that can take 6-12 months to complete. 
            Check your destination's requirements immediately after booking travel.
          </p>
        </AlertBox>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-12">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{easyCountries.length}</div>
          <div className="text-slate-600 text-sm">Easy entry countries</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{moderateCountries.length}</div>
          <div className="text-slate-600 text-sm">Permit required</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{strictCountries.length}</div>
          <div className="text-slate-600 text-sm">Quarantine countries</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">21 days</div>
          <div className="text-slate-600 text-sm">Min. rabies waiting period</div>
        </div>
      </div>

      {/* Strict Countries Warning */}
      {strictCountries.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Strict Countries - Quarantine Required
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <p className="text-red-800">
              These countries have mandatory quarantine periods, extensive documentation requirements, 
              and/or blood tests. <strong>Start preparation 6-12 months before travel.</strong>
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {strictCountries.map(country => (
              <CountryCard key={country.slug} country={country} />
            ))}
          </div>
        </section>
      )}

      {/* Moderate Countries */}
      {moderateCountries.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-600" />
            Moderate - Import Permit Required
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {moderateCountries.map(country => (
              <CountryCard key={country.slug} country={country} />
            ))}
          </div>
        </section>
      )}

      {/* Easy Countries */}
      {easyCountries.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Easier Entry - No Quarantine, No Permit
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {easyCountries.map(country => (
              <CountryCard key={country.slug} country={country} />
            ))}
          </div>
        </section>
      )}

      {/* Universal Requirements */}
      <section className="mt-16 bg-slate-100 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          Universal Requirements (Almost All Countries)
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">✅ Required by Most Countries</h3>
            <ul className="space-y-2 text-slate-700">
              <li>• ISO-compliant microchip (implanted BEFORE rabies vaccine)</li>
              <li>• Rabies vaccination (at least 21 days before travel)</li>
              <li>• Veterinary health certificate (within 10 days of travel)</li>
              <li>• Pet must be healthy and fit to fly</li>
              <li>• IATA-compliant crate for cargo travel</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">⚠️ Country-Specific Requirements</h3>
            <ul className="space-y-2 text-slate-700">
              <li>• Import permits (Australia, Japan, Singapore, etc.)</li>
              <li>• Blood tests / rabies titer (Japan, Australia, etc.)</li>
              <li>• Tapeworm treatment (UK, Ireland, Norway, Finland)</li>
              <li>• Additional vaccinations (some countries)</li>
              <li>• Quarantine (Australia, Hawaii, etc.)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="mt-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600 mb-8">
            International Pet Travel: What You Need to Know
          </h2>
          
          <div className="space-y-6">
            <p className="text-lg text-slate-700 leading-relaxed">
              Moving to a new country with your pet requires careful planning. Every country has its own 
              set of rules governing pet imports, designed to protect local animal populations from disease. 
              These regulations can range from simple (showing a rabies certificate) to extremely complex 
              (months of preparation, blood tests, and quarantine).
            </p>
            
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border border-red-100">
              <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                The Rabies-Free Island Problem
              </h3>
              <p className="text-red-700 leading-relaxed">
                Countries and territories that are rabies-free (Australia, New Zealand, Japan, Hawaii, UK, Ireland) 
                have the strictest requirements. They've invested heavily in maintaining their rabies-free status 
                and require extensive proof that incoming animals don't carry the disease. This typically means 
                rabies antibody blood tests and waiting periods of 90-180 days.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                EU Pet Passport System
              </h3>
              <p className="text-blue-700 leading-relaxed">
                Travel within the European Union is simplified by the EU Pet Passport system. EU residents can 
                obtain a passport for their pet that tracks vaccinations and health information, allowing 
                relatively easy travel between member states. Non-EU residents must use Animal Health Certificates 
                instead, which are valid for a single trip.
              </p>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-100">
              <h3 className="text-xl font-bold text-amber-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Common Mistakes to Avoid
              </h3>
              <p className="text-amber-700 leading-relaxed">
                The most frequent errors in international pet travel include: getting the microchip AFTER the 
                rabies vaccine (must be before), having expired health certificates (usually valid only 10 days), 
                missing the tapeworm treatment window (for UK/Ireland), and underestimating preparation time 
                for strict countries. Always research your destination's requirements at least 6 months before 
                planned travel.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default DestinationsHub;
